import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';
import useApiStable from '../../utils/useApiStable';
import { canManageDrinks } from '../../utils/userUtils';
import DrinkItem from './DrinkItem';
import DrinkAdmin from './DrinkAdmin';
import DrinkPaidPanel from './DrinkPaidPanel';
import '../../styles/DrinkList.css';

const QUANTITIES_KEY = 'drinks_quantities';
const FREQUENCY_KEY = 'drinks_frequency';

function DrinkList() {
    const [drinks, setDrinks] = useState([]);
    const [orderedIds, setOrderedIds] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [showPaidPanel, setShowPaidPanel] = useState(false);
    const [paidTotal, setPaidTotal] = useState('0.00');
    const [error, setError] = useState('');
    const { user } = useContext(AuthContext);
    const api = useApiStable();

    const loadDrinks = () => {
        setError('');
        return api.get('/drinks')
            .then(data => {
                setDrinks(Array.isArray(data) ? data : []);
                return data;
            })
            .catch(err => setError(err.message || 'Fehler beim Laden der Getränke'));
    };

    useEffect(() => {
        const storedQuantities = JSON.parse(localStorage.getItem(QUANTITIES_KEY) || '{}');
        setQuantities(storedQuantities);

        api.get('/drinks')
            .then(data => {
                const freq = JSON.parse(localStorage.getItem(FREQUENCY_KEY) || '{}');
                // Sortierung einmalig berechnen und für Seitenlebensdauer fixieren
                const sorted = [...(Array.isArray(data) ? data : [])].sort(
                    (a, b) => (freq[b.id] || 0) - (freq[a.id] || 0)
                );
                setDrinks(Array.isArray(data) ? data : []);
                setOrderedIds(sorted.map(d => d.id));
            })
            .catch(err => setError(err.message || 'Fehler beim Laden der Getränke'));
    }, []); // eslint-disable-line

    const handleChange = (drinkId, delta) => {
        setQuantities(prev => {
            const next = { ...prev, [drinkId]: Math.max(0, (prev[drinkId] || 0) + delta) };
            localStorage.setItem(QUANTITIES_KEY, JSON.stringify(next));
            if (delta !== 0) {
                const freq = JSON.parse(localStorage.getItem(FREQUENCY_KEY) || '{}');
                freq[drinkId] = Math.max(0, (freq[drinkId] || 0) + delta);
                localStorage.setItem(FREQUENCY_KEY, JSON.stringify(freq));
            }
            return next;
        });
    };

    const total = drinks
        .reduce((sum, d) => sum + Number(d.price) * (quantities[d.id] || 0), 0)
        .toFixed(2);

    const handlePaypalClick = () => {
        setPaidTotal(total);
        setShowPaidPanel(true);
    };

    const handlePaypalPaid = () => {
        localStorage.removeItem(QUANTITIES_KEY);
        setQuantities({});
        setShowPaidPanel(false);
    };

    // Sortierreihenfolge anwenden; neue Getränke ans Ende
    const orderedDrinks = [
        ...orderedIds.map(id => drinks.find(d => d.id === id)).filter(Boolean),
        ...drinks.filter(d => !orderedIds.includes(d.id)),
    ];

    const hasItems = parseFloat(total) > 0;

    return (
        <div className="drink-list-page">
            <h1>Getränke</h1>
            {error && <div className="drink-error">{error}</div>}
            <div className="drink-summary">
                <span className="drink-total">Gesamt: {total} €</span>
                <div className="drink-summary-actions">
                    <button
                        className="drink-pay-btn drink-pay-btn--paypal"
                        onClick={handlePaypalClick}
                        disabled={!hasItems}
                    >
                        Bezahlen
                    </button>
                </div>
            </div>
            <div className="drink-list">
                {orderedDrinks.length === 0 && !error && (
                    <p className="drink-empty">Keine Getränke verfügbar.</p>
                )}
                {orderedDrinks.map(drink => (
                    <DrinkItem
                        key={drink.id}
                        drink={drink}
                        quantity={quantities[drink.id] || 0}
                        onChange={handleChange}
                    />
                ))}
            </div>
            {showPaidPanel && (
                <DrinkPaidPanel
                    total={paidTotal}
                    onClose={() => setShowPaidPanel(false)}
                    onPaid={handlePaypalPaid}
                />
            )}
            {canManageDrinks(user) && (
                <DrinkAdmin drinks={drinks} onRefresh={loadDrinks} />
            )}
        </div>
    );
}

export default DrinkList;
